import { useCallback } from 'react';
import {
  SourceVariableType,
  VariableNameAndType,
} from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { VariableErrorTypes } from 'pages/AutomationEngine/dialogs/VariableWizard/VariableWizardContext';
import { CommonVariableDefinitionType } from 'pages/AutomationEngine/types';

export const useVariableLogic = (
  variable: VariableNameAndType,
  staticIndex: number,
  sources: SourceVariableType[],
  setVariableSources: (sources: SourceVariableType[]) => void,
  forInitialize: boolean,
  errors: VariableErrorTypes,
  setErrors: (nextErrors: VariableErrorTypes) => void,
  getTypeLookUp: (
    type: CommonVariableDefinitionType
  ) => SourceVariableType | undefined
) => {
  const addVariableSource = useCallback(
    ({ value: source }: { value: CommonVariableDefinitionType }) => {
      let type = getTypeLookUp(source);
      if (!type) {
        return;
      }
      if (source === 'static') {
        type = {
          ...type,
          staticVariable: {
            ...type.staticVariable!,
            valueCategory: variable?.dataSubtype!,
            value: type.staticVariable!.value,
          },
        };
      }

      const newSource = {
        ...type,
        id: `new-${source}-${Date.now()}`,
        sourceSubtype: variable?.dataType, // keep this in synch for now going fwd may have it's own selector
        forInitialize,
      };

      if (staticIndex === -1) {
        // no static
        setVariableSources([...sources, newSource]);
      } else {
        setVariableSources(sources.toSpliced(staticIndex, 0, newSource));
      }
    },
    [
      getTypeLookUp,
      variable?.dataType,
      variable?.dataSubtype,
      forInitialize,
      staticIndex,
      setVariableSources,
      sources,
    ]
  );

  const moveVariableSource = useCallback(
    (fromIndex: number, offSet: number) => {
      const newArray = sources.map((variableSource, index) => {
        const toIndex = fromIndex + offSet;
        if (index === fromIndex) {
          return sources[toIndex];
        }
        if (index === toIndex) {
          return sources[fromIndex];
        }
        return variableSource;
      });
      setVariableSources(newArray);
      return newArray;
    },
    [sources, setVariableSources]
  );

  const deleteVariableSource = useCallback(
    (index: number) => {
      const { id } = sources[index];
      const newArray = sources.toSpliced(index, 1);
      setVariableSources(newArray);
      const { [id]: _, ...nextErrors } = errors;
      setErrors(nextErrors);

      return newArray;
    },
    [sources, setVariableSources, errors, setErrors]
  );

  const updateVariableSource = useCallback(
    (update: SourceVariableType) => {
      const variableIndex = (sources || []).findIndex(
        ({ id }) => id === update.id
      );

      if (variableIndex !== -1) {
        const newArray = sources.toSpliced(variableIndex, 1, update);
        setVariableSources(newArray);
      } else {
        throw 'Variable not found';
      }
    },
    [setVariableSources, sources]
  );

  return {
    addVariableSource,
    moveVariableSource,
    deleteVariableSource,
    updateVariableSource,
  };
};
